/**
 * Data model for the slim DIN-276 tree (administration, root-admin only).
 *
 * Source: userAPI.getDIN276Metadata() — the server parses the extended CSV
 * (papaparse), strips U+200C from codes, and returns categories IN DOCUMENT
 * ORDER with { code, level, name, comments, kind, source, fields, slots, refs }.
 *
 * Two downward relations:
 *  1) Classification (the DIN tree) — built from `level`: each node is a child
 *     of the last seen node with level = current − 1 (CSV is hierarchically
 *     ordered). Namespaced rows (K. / T. / F.) carry no level and never become
 *     tree roots — they appear only as slot targets (parts).
 *  2) Composition (parts) — each `slot` (propertyType=Objekt) is a hat-Teil edge
 *     to `refCode` with a role name (`property`) and a cardinality.
 */

export interface DinAttr {
  name: string;
  type: string; // friendly: Text | Zeiger | Dezimalzahl | Ganzzahl | Boolean | …
  unit: string | null;
  enum: string[];
  source?: string;
  measured: boolean; // numeric + unit → conformance-relevant
  typeUnclear: boolean;
  /** The fields of a structure (customer attributes only), shown indented below it. */
  children?: DinAttr[];
  /** Added by the customer (Betreiberpflichten → Datenmodell), not given by the norm. */
  own?: boolean;
  /** Computed, never entered (e.g. "co2e" — tri-model co2eTonnesOf). */
  derived?: string;
}
export interface DinSlot {
  name: string;
  target: string;
  cardinality: string;
  source?: string;
}
export interface DinRef {
  name: string;
  target: string;
  source?: string;
}
export interface DinNode {
  code: string;
  label: string;
  level: number | null;
  kind: string;
  source?: string;
  comments?: string;
  synthetic: boolean; // provisional placeholder node (pipeline-synthesized, not yet confirmed)
  icon?: string; // DIN 276 category icon (Material Symbol name) from metadata
  color?: string; // DIN 276 category colour (hex) from metadata
  attrs: DinAttr[];
  slots: DinSlot[];
  refs: DinRef[];
  children: string[]; // classification children codes
  reuse: number; // number of incoming composition slots (where-used count)
}
export interface UsedBy {
  owner: string;
  slot: string;
  cardinality: string;
}
export interface DinTree {
  byCode: Record<string, DinNode>;
  roots: string[];
  parentOf: Record<string, string>;
  usedBy: Record<string, UsedBy[]>;
  nodeCount: number;
  slotCount: number;
}

// Friendly type label from the server's mapped type + raw type fallbacks.
const TYPE_LABEL: Record<string, string> = {
  number: "Dezimalzahl",
  string: "Text",
  // Customer attributes (Betreiberpflichten → Datenmodell) add dates and structures.
  date: "Datum",
  structure: "Struktur",
  // "Zeiger" (pointer) in the source CSV is really an enum / fixed selection.
  enum: "Auswahl",
  text: "Text",
  boolean: "Boolean",
};
const NUMERIC = new Set(["number", "Dezimalzahl", "Ganzzahl", "decimal", "integer"]);
const KNOWN = new Set([
  "number", "enum", "text", "boolean",
  "Text", "Zeiger", "Dezimalzahl", "Ganzzahl", "Boolean",
]);
/** The `source` of an attribute the customer added (server: data/tenantDin276.ts). */
export const TENANT_SOURCE = "tenant";
const normType = (raw?: string): string => {
  const r = (raw || "").trim();
  if (r === "BOOL") return "Boolean";
  if (r === "NUM") return "Dezimalzahl";
  return r;
};
const splitEnum = (vals?: string[]): string[] =>
  (vals || [])
    .flatMap((v) => String(v).split(/[|;,]/))
    .map((v) => v.trim())
    .filter(Boolean);

/** One field of the server's metadata as an attribute; a structure's fields become `children`. */
const toAttr = (f: any): DinAttr => {
  const rawType = normType(f.rawType);
  const friendly = f.integer ? "Ganzzahl" : TYPE_LABEL[f.type] || rawType || f.type || "Text";
  const numeric = NUMERIC.has(f.type) || NUMERIC.has(rawType);
  const typeUnclear = !!rawType && !KNOWN.has(rawType) && rawType.length > 14;
  // Unit fallback: pull from a trailing "[…]" in the attribute label.
  let name = String(f.name ?? "");
  let unit: string | null = f.unit ?? null;
  if (!unit) {
    const m = /\[([^\]]+)\]\s*$/.exec(name);
    if (m) {
      unit = m[1].trim();
      name = name.replace(/\s*\[[^\]]+\]\s*$/, "").trim();
    }
  }
  return {
    name,
    type: typeUnclear ? "Text" : friendly,
    unit,
    enum: splitEnum(f.values),
    source: f.source,
    measured: numeric && !!unit,
    typeUnclear,
    ...(f.source === TENANT_SOURCE ? { own: true } : {}),
    ...(Array.isArray(f.fields) ? { children: f.fields.map(toAttr) } : {}),
    ...(f.derived ? { derived: String(f.derived) } : {}),
  };
};

export function buildTree(categories: any[]): DinTree {
  const byCode: Record<string, DinNode> = {};
  let slotCount = 0;

  for (const c of categories || []) {
    const code = String(c.code || "").trim();
    if (!code) continue;
    const lvl = parseInt(String(c.level ?? "").trim(), 10);
    const attrs: DinAttr[] = (c.fields || []).map(toAttr);
    byCode[code] = {
      code,
      label: c.name || code,
      level: Number.isFinite(lvl) ? lvl : null,
      kind: (c.kind || "category").trim(),
      source: c.source,
      comments: c.comments,
      synthetic: c.synthetic === true,
      icon: c.icon,
      color: c.color,
      attrs,
      slots: (c.slots || [])
        .filter((s: any) => s.refCode)
        .map((s: any): DinSlot => ({
          name: s.name,
          target: String(s.refCode).trim(),
          cardinality: String(s.cardinality || "").trim(),
          source: s.source,
        })),
      refs: (c.refs || []).map((r: any): DinRef => ({ name: r.name, target: r.target, source: r.source })),
      children: [],
      reuse: 0,
    };
  }

  // Classification tree — derived from the DIN code structure, NOT document
  // order. The CSV's GEFMA depth-4/5 rows are appended in a block (not nested
  // under their DIN-3 parents), so a "last node at level−1" heuristic drops
  // them (they became roots). The code itself encodes the hierarchy:
  // 300 → 330 → 334 → 334.40 → 334.41, and 350 → 351 → 351.10.
  // Nodes with no level (K. / T. / F.) are never tree nodes — only slot targets.
  const parentCodeOf = (code: string): string | null => {
    const dot = code.indexOf(".");
    if (dot >= 0) {
      const head = code.slice(0, dot);
      const dec = code.slice(dot + 1);
      if (dec.length === 2 && dec[1] !== "0") return `${head}.${dec[0]}0`; // 334.41 → 334.40
      if (dec.length > 1) return `${head}.${dec.slice(0, -1)}`; // 351.10 → 351.1
      return head; // 334.4 → 334
    }
    if (/^\d{3}$/.test(code)) {
      if (code[2] !== "0") return code.slice(0, 2) + "0"; // 334 → 330
      if (code[1] !== "0") return code[0] + "00"; // 330 → 300
      return null; // 300 → root
    }
    return null;
  };
  const roots: string[] = [];
  const parentOf: Record<string, string> = {};
  for (const node of Object.values(byCode)) {
    if (node.level == null) continue;
    let p = parentCodeOf(node.code);
    while (p && !byCode[p]) p = parentCodeOf(p); // skip missing intermediate codes
    if (p && byCode[p]) {
      byCode[p].children.push(node.code);
      parentOf[node.code] = p;
    } else {
      roots.push(node.code);
    }
  }
  // Sort children & roots by numeric code (300 < 330 < 334 < 334.40 < 334.41).
  const sortKey = (c: string) => c.split(".").map((s) => s.padStart(4, "0")).join(".");
  const byKey = (a: string, b: string) => sortKey(a).localeCompare(sortKey(b));
  roots.sort(byKey);
  for (const n of Object.values(byCode)) n.children.sort(byKey);

  // Where-used (in-degree of composition slots).
  const usedBy: Record<string, UsedBy[]> = {};
  for (const node of Object.values(byCode)) {
    for (const s of node.slots) {
      slotCount += 1;
      if (!byCode[s.target]) continue;
      byCode[s.target].reuse += 1;
      (usedBy[s.target] ||= []).push({ owner: node.code, slot: s.name, cardinality: s.cardinality });
    }
  }

  return { byCode, roots, parentOf, usedBy, nodeCount: Object.keys(byCode).length, slotCount };
}

// ── Units ─────────────────────────────────────────────────────────────────────
const UNIT_LONG: Record<string, string> = {
  mm: "Millimeter", cm: "Zentimeter", m: "Meter",
  kN: "Kilonewton", "kN/m": "Kilonewton pro Meter", N: "Newton", "N/m": "Newton pro Meter",
  kg: "Kilogramm", "°": "Grad", "%": "Prozent", s: "Sekunden", min: "Minuten", Pa: "Pascal", bar: "Bar",
};
export const expandUnit = (u?: string | null): string => (u && UNIT_LONG[u]) || u || "";

// ── Normen-Register (kompakt) ─────────────────────────────────────────────────
export interface NormEntry {
  id: string;
  title: string;
  governs: string;
  harmonized: boolean; // hEN/hEPN
  href?: string;
}
const NORMS: NormEntry[] = [
  { id: "DIN 18065", title: "Gebäudetreppen", governs: "Treppe, Handlauf, Geländer-Maße", harmonized: false },
  { id: "DIN 18040", title: "Barrierefreies Bauen", governs: "2. Handlauf, Überstand", harmonized: false },
  { id: "DIN 18008-4", title: "Glas im Bauwesen, absturzsichernd", governs: "Verglasung Kat A/B/C", harmonized: false },
  { id: "EN 14351-1", title: "Fenster und Außentüren", governs: "Fenster/Türen (harmonisiert)", harmonized: true },
  { id: "EN 16034", title: "Feuer-/Rauchschutzabschlüsse", governs: "Brandschutztür", harmonized: true },
  { id: "EN 1125", title: "Panikverschlüsse", governs: "Verschluss (Panik)", harmonized: true },
  { id: "EN 179", title: "Notausgangsverschlüsse", governs: "Verschluss (Notausgang)", harmonized: true },
  { id: "DIN 14677", title: "Instandhaltung Feststellanlagen", governs: "FSA (zyklisch)", harmonized: false },
  { id: "ASR A1.7", title: "Türen und Tore", governs: "kraftbetätigt (zyklisch)", harmonized: false },
  { id: "ASR A2.1", title: "Schutz vor Absturz", governs: "Umwehrung, Absturzkante, Fußleiste", harmonized: false },
  { id: "EN 795", title: "Anschlageinrichtungen", governs: "Anschlagpunkt (zyklisch)", harmonized: false },
  { id: "EN 353-1", title: "Steigschutz", governs: "Steigleiter (zyklisch)", harmonized: false },
  { id: "EltVTR", title: "Elektr. Verriegelung Rettungsweg", governs: "Fluchttürsteuerung", harmonized: false },
  { id: "EN 1991-1-1", title: "Einwirkungen (Eurocode 1)", governs: "Holmlast, Befestigung", harmonized: false },
  { id: "GEFMA/REG-IS", title: "FM-Regelwerk", governs: "Inspektion/Wartung (FM)", harmonized: false },
  { id: "TRI", title: "TRI-Modellerweiterung", governs: "Bauteile, Komposition", harmonized: false },
];
export const matchNorm = (source?: string): NormEntry | undefined => {
  const up = (source || "").trim().toUpperCase();
  if (!up) return undefined;
  let best: NormEntry | undefined;
  for (const n of NORMS) {
    if (up.includes(n.id.toUpperCase()) && (!best || n.id.length > best.id.length)) best = n;
  }
  return best;
};
export const isHarmonized = (source?: string): boolean => {
  const n = matchNorm(source);
  return !!n?.harmonized || /h?EPN|hEN/i.test(source || "");
};
